import React from 'react';
import { Box, Typography, Divider } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useLocation, useNavigate } from 'react-router-dom';
import Dashboard from './Dashboard/Dashboard';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ListAltIcon from '@mui/icons-material/ListAlt';
import InsightsIcon from '@mui/icons-material/Insights';
import ChatIcon from '@mui/icons-material/Chat';
import DescriptionIcon from '@mui/icons-material/Description';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import HelpIcon from '@mui/icons-material/Help';
import SettingsIcon from '@mui/icons-material/Settings';
import ListingManagement from './Managment/ListingManagement';
import MyContact from '../MyAccount/MyContact/MyContact';
import Document from './Document/Document';
import Onboarding from './Onboarding/Onboarding';
import MarketAnalytics from './MarketAnalytics/MarketAnalytics';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';

const Sidebar = styled(Box)(({ theme }) => ({
  width: 250,
  minWidth: '250px',
  padding: theme.spacing(3),
  borderRight: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  flexDirection: 'column',
  height: '100vh'
}));

const MainNavigation = styled(Box)({
  flexGrow: 1,
  overflowY: 'auto'
});

const BottomNavigation = styled(Box)(({ theme }) => ({
  marginTop: 'auto',
  paddingTop: theme.spacing(2)
}));

const NavItem = styled(Box)(({ theme, active }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(1, 2),
  cursor: 'pointer',
  marginBottom: '10px',
  borderRadius: theme.shape.borderRadius,
  transition: theme.transitions.create(['background-color', 'font-weight'], {
    duration: theme.transitions.duration.shortest
  }),
  ...(active && {
    backgroundColor: '#D2DEFF',
    fontWeight: 'bold'
  }),
  '&:hover': {
    backgroundColor: '#D2DEFF',
    fontWeight: 'bold'
  }
}));

const NavItemText = styled(Typography)(({ theme, active }) => ({
  marginLeft: theme.spacing(1),
  fontSize: '14px',
  fontWeight: active ? '700' : '500'
}));

const GroupDivider = styled(Divider)(({ theme }) => ({
  margin: theme.spacing(2, 0)
}));

const OwnerAccount = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const searchTerm = queryParams.get('section') || '';

  const handleClick = (link) => {
    navigate(`/owner-account?section=${link}`);
  };

  const mainNavGroups = [
    {
      items: [
        {
          name: t('Dashboard'),
          icon: (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
            >
              <path
                d="M9.375 4.375V8.125C9.375 8.45652 9.2433 8.77446 9.00888 9.00888C8.77446 9.2433 8.45652 9.375 8.125 9.375H4.375C4.04348 9.375 3.72554 9.2433 3.49112 9.00888C3.2567 8.77446 3.125 8.45652 3.125 8.125V4.375C3.125 4.04348 3.2567 3.72554 3.49112 3.49112C3.72554 3.2567 4.04348 3.125 4.375 3.125H8.125C8.45652 3.125 8.77446 3.2567 9.00888 3.49112C9.2433 3.72554 9.375 4.04348 9.375 4.375ZM15.625 3.125H11.875C11.5435 3.125 11.2255 3.2567 10.9911 3.49112C10.7567 3.72554 10.625 4.04348 10.625 4.375V8.125C10.625 8.45652 10.7567 8.77446 10.9911 9.00888C11.2255 9.2433 11.5435 9.375 11.875 9.375H15.625C15.9565 9.375 16.2745 9.2433 16.5089 9.00888C16.7433 8.77446 16.875 8.45652 16.875 8.125V4.375C16.875 4.04348 16.7433 3.72554 16.5089 3.49112C16.2745 3.2567 15.9565 3.125 15.625 3.125ZM8.125 10.625H4.375C4.04348 10.625 3.72554 10.7567 3.49112 10.9911C3.2567 11.2255 3.125 11.5435 3.125 11.875V15.625C3.125 15.9565 3.2567 16.2745 3.49112 16.5089C3.72554 16.7433 4.04348 16.875 4.375 16.875H8.125C8.45652 16.875 8.77446 16.7433 9.00888 16.5089C9.2433 16.2745 9.375 15.9565 9.375 15.625V11.875C9.375 11.5435 9.2433 11.2255 9.00888 10.9911C8.77446 10.7567 8.45652 10.625 8.125 10.625ZM15.625 10.625H11.875C11.5435 10.625 11.2255 10.7567 10.9911 10.9911C10.7567 11.2255 10.625 11.5435 10.625 11.875V15.625C10.625 15.9565 10.7567 16.2745 10.9911 16.5089C11.2255 16.7433 11.5435 16.875 11.875 16.875H15.625C15.9565 16.875 16.2745 16.7433 16.5089 16.5089C16.7433 16.2745 16.875 15.9565 16.875 15.625V11.875C16.875 11.5435 16.7433 11.2255 16.5089 10.9911C16.2745 10.7567 15.9565 10.625 15.625 10.625Z"
                fill="#202020"
              />
            </svg>
          ),
          link: 'dashboard'
        },
        {
          name: t('Listing Management'),
          icon: (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
            >
              <path
                d="M17.1336 8.49083L10.8836 2.24083C10.6492 2.00658 10.3314 1.875 10 1.875C9.66863 1.875 9.35081 2.00658 9.11641 2.24083L2.86641 8.49083C2.74978 8.6066 2.65732 8.7444 2.59442 8.89623C2.53151 9.04805 2.49942 9.21086 2.50001 9.3752V16.8752C2.50001 17.041 2.56586 17.1999 2.68307 17.3171C2.80028 17.4344 2.95925 17.5002 3.12501 17.5002H8.12501C8.29077 17.5002 8.44974 17.4344 8.56695 17.3171C8.68416 17.1999 8.75001 17.041 8.75001 16.8752V12.5002H11.25V16.8752C11.25 17.041 11.3159 17.1999 11.4331 17.3171C11.5503 17.4344 11.7092 17.5002 11.875 17.5002H16.875C17.0408 17.5002 17.1997 17.4344 17.317 17.3171C17.4342 17.1999 17.5 17.041 17.5 16.8752V9.3752C17.5006 9.21086 17.4685 9.04805 17.4056 8.89623C17.3427 8.7444 17.2502 8.6066 17.1336 8.49083ZM16.25 16.2502H12.5V11.8752C12.5 11.7094 12.4342 11.5505 12.3169 11.4333C12.1997 11.316 12.0408 11.2502 11.875 11.2502H8.12501C7.95925 11.2502 7.80028 11.316 7.68307 11.4333C7.56586 11.5505 7.50001 11.7094 7.50001 11.8752V16.2502H3.75001V9.3752L10 3.1252L16.25 9.3752V16.2502Z"
                fill="#202020"
              />
            </svg>
          ),
          link: 'listing'
        },
        {
          name: t('Analytics and Insights'),
          icon: (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
            >
              <path
                d="M10 1.875C8.39303 1.875 6.82214 2.35152 5.486 3.24431C4.14985 4.1371 3.10844 5.40605 2.49348 6.8907C1.87852 8.37535 1.71762 10.009 2.03112 11.5851C2.34463 13.1612 3.11846 14.6089 4.25476 15.7452C5.39106 16.8815 6.8388 17.6554 8.4149 17.9689C9.99099 18.2824 11.6247 18.1215 13.1093 17.5065C14.594 16.8916 15.8629 15.8502 16.7557 14.514C17.6485 13.1779 18.125 11.607 18.125 10C18.1227 7.84581 17.266 5.78051 15.7427 4.25727C14.2195 2.73403 12.1542 1.87727 10 1.875ZM15.6148 6.03672L10.625 8.91719V3.15391C11.6158 3.24474 12.5751 3.54954 13.4366 4.04726C14.2981 4.54498 15.0413 5.22376 15.6148 6.03672ZM9.375 3.15391V9.63828L3.7586 12.8805C3.29753 11.8811 3.08313 10.7857 3.1334 9.68628C3.18368 8.58685 3.49716 7.51556 4.04751 6.56247C4.59785 5.60938 5.36896 4.80234 6.29603 4.2092C7.22309 3.61606 8.27901 3.25415 9.375 3.15391ZM10 16.875C8.89937 16.8745 7.81493 16.6099 6.83786 16.1032C5.86079 15.5965 5.01965 14.8626 4.38516 13.9633L16.2414 7.11875C16.7249 8.16621 16.937 9.3185 16.8582 10.4695C16.7795 11.6204 16.4123 12.7331 15.7906 13.7049C15.1689 14.6767 14.3126 15.4765 13.3007 16.0305C12.2888 16.5844 11.1537 16.8749 10 16.875Z"
                fill="#202020"
              />
            </svg>
          ),
          link: 'analytics'
        }
      ]
    },
    {
      items: [
        {
          name: t('Communication Hub'),
          icon: (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
            >
              <path
                d="M13.2477 5.67198C12.682 4.34773 11.6765 3.25941 10.401 2.59102C9.12553 1.92263 7.65839 1.71518 6.2476 2.00373C4.83682 2.29228 3.56895 3.05913 2.65834 4.17465C1.74773 5.29016 1.25025 6.68589 1.25 8.12588V13.1259C1.25 13.4574 1.3817 13.7753 1.61612 14.0098C1.85054 14.2442 2.16848 14.3759 2.5 14.3759H6.77109C7.25882 15.4897 8.06026 16.4374 9.07758 17.1033C10.0949 17.7692 11.2841 18.1245 12.5 18.1259H17.5C17.8315 18.1259 18.1495 17.9942 18.3839 17.7598C18.6183 17.5253 18.75 17.2074 18.75 16.8759V11.8759C18.7497 10.348 18.1897 8.87308 17.1759 7.72998C16.1621 6.58688 14.7646 5.85476 13.2477 5.67198ZM2.5 8.12588C2.5 7.13698 2.79324 6.17028 3.34265 5.34803C3.89206 4.52579 4.67295 3.88493 5.58658 3.50649C6.50021 3.12805 7.50555 3.02903 8.47545 3.22196C9.44536 3.41488 10.3363 3.89109 11.0355 4.59035C11.7348 5.28961 12.211 6.18053 12.4039 7.15043C12.5969 8.12034 12.4978 9.12567 12.1194 10.0393C11.741 10.9529 11.1001 11.7338 10.2779 12.2832C9.4556 12.8326 8.48891 13.1259 7.5 13.1259H2.5V8.12588ZM17.5 16.8759H12.5C11.6159 16.8749 10.7477 16.64 9.98375 16.195C9.21976 15.75 8.5871 15.1108 8.15 14.3423C9.00603 14.2535 9.83445 13.9886 10.5833 13.5645C11.3321 13.1403 11.9852 12.5659 12.5015 11.8773C13.0178 11.1888 13.3862 10.4009 13.5836 9.56324C13.7809 8.72555 13.803 7.85611 13.6484 7.00948C14.7447 7.2682 15.7216 7.88953 16.4206 8.77279C17.1197 9.65604 17.5 10.7495 17.5 11.8759V16.8759Z"
                fill="#202020"
              />
            </svg>
          ),
          link: 'communication'
        },
        {
          name: t('DMS'),
          icon: (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
            >
              <path
                d="M16.6922 5.18281L13.5672 2.05781C13.5091 1.99979 13.4402 1.95378 13.3643 1.92241C13.2884 1.89105 13.2071 1.87494 13.125 1.875H6.875C6.54348 1.875 6.22554 2.0067 5.99112 2.24112C5.7567 2.47554 5.625 2.79348 5.625 3.125V4.375H4.375C4.04348 4.375 3.72554 4.5067 3.49112 4.74112C3.2567 4.97554 3.125 5.29348 3.125 5.625V16.875C3.125 17.2065 3.2567 17.5245 3.49112 17.7589C3.72554 17.9933 4.04348 18.125 4.375 18.125H13.125C13.4565 18.125 13.7745 17.9933 14.0089 17.7589C14.2433 17.5245 14.375 17.2065 14.375 16.875V15.625H15.625C15.9565 15.625 16.2745 15.4933 16.5089 15.2589C16.7433 15.0245 16.875 14.7065 16.875 14.375V5.625C16.8751 5.5429 16.859 5.46159 16.8276 5.38572C16.7962 5.30985 16.7502 5.2409 16.6922 5.18281ZM13.125 16.875H4.375V5.625H10.3664L13.125 8.38359V14.9875C13.125 14.9922 13.125 14.9961 13.125 15C13.125 15.0039 13.125 15.0078 13.125 15.0125V16.875ZM15.625 14.375H14.375V8.125C14.3751 8.0429 14.359 7.96159 14.3276 7.88572C14.2962 7.80985 14.2502 7.7409 14.1922 7.68281L11.0672 4.55781C11.0091 4.49979 10.9402 4.45378 10.8643 4.42241C10.7884 4.39105 10.7071 4.37494 10.625 4.375H6.875V3.125H12.8664L15.625 5.88359V14.375ZM11.25 11.875C11.25 12.0408 11.1842 12.1997 11.0669 12.3169C10.9497 12.4342 10.7908 12.5 10.625 12.5H6.875C6.70924 12.5 6.55027 12.4342 6.43306 12.3169C6.31585 12.1997 6.25 12.0408 6.25 11.875C6.25 11.7092 6.31585 11.5503 6.43306 11.4331C6.55027 11.3158 6.70924 11.25 6.875 11.25H10.625C10.7908 11.25 10.9497 11.3158 11.0669 11.4331C11.1842 11.5503 11.25 11.7092 11.25 11.875ZM11.25 14.375C11.25 14.5408 11.1842 14.6997 11.0669 14.8169C10.9497 14.9342 10.7908 15 10.625 15H6.875C6.70924 15 6.55027 14.9342 6.43306 14.8169C6.31585 14.6997 6.25 14.5408 6.25 14.375C6.25 14.2092 6.31585 14.0503 6.43306 13.9331C6.55027 13.8158 6.70924 13.75 6.875 13.75H10.625C10.7908 13.75 10.9497 13.8158 11.0669 13.9331C11.1842 14.0503 11.25 14.2092 11.25 14.375Z"
                fill="#202020"
              />
            </svg>
          ),
          link: 'dms'
        }
      ]
    },
    {
      items: [
        {
          name: t('Onboarding for Landlords'),
          icon: (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
            >
              <path
                d="M18.125 3.75H12.5C12.0149 3.75 11.5364 3.86295 11.1025 4.07991C10.6685 4.29688 10.2911 4.61189 10 5C9.70892 4.61189 9.33147 4.29688 8.89754 4.07991C8.46362 3.86295 7.98514 3.75 7.5 3.75H1.875C1.70924 3.75 1.55027 3.81585 1.43306 3.93306C1.31585 4.05027 1.25 4.20924 1.25 4.375V15.625C1.25 15.7908 1.31585 15.9497 1.43306 16.0669C1.55027 16.1842 1.70924 16.25 1.875 16.25H7.5C7.99728 16.25 8.47419 16.4475 8.82583 16.7992C9.17746 17.1508 9.375 17.6277 9.375 18.125C9.375 18.2908 9.44085 18.4497 9.55806 18.5669C9.67527 18.6842 9.83424 18.75 10 18.75C10.1658 18.75 10.3247 18.6842 10.4419 18.5669C10.5592 18.4497 10.625 18.2908 10.625 18.125C10.625 17.6277 10.8225 17.1508 11.1742 16.7992C11.5258 16.4475 12.0027 16.25 12.5 16.25H18.125C18.2908 16.25 18.4497 16.1842 18.5669 16.0669C18.6842 15.9497 18.75 15.7908 18.75 15.625V4.375C18.75 4.20924 18.6842 4.05027 18.5669 3.93306C18.4497 3.81585 18.2908 3.75 18.125 3.75ZM7.5 15H2.5V5H7.5C7.99728 5 8.47419 5.19754 8.82583 5.54917C9.17746 5.90081 9.375 6.37772 9.375 6.875V15.625C8.83458 15.2183 8.17633 14.9989 7.5 15ZM17.5 15H12.5C11.8237 14.9989 11.1654 15.2183 10.625 15.625V6.875C10.625 6.37772 10.8225 5.90081 11.1742 5.54917C11.5258 5.19754 12.0027 5 12.5 5H17.5V15Z"
                fill="#202020"
              />
            </svg>
          ),
          link: 'onboarding'
        }
      ]
    }
  ];

  // const accountNavGroup = {
  //   items: [
  //     {
  //       name: t('Help'),
  //       icon: (
  //         <svg
  //           xmlns="http://www.w3.org/2000/svg"
  //           width="20"
  //           height="20"
  //           viewBox="0 0 20 20"
  //           fill="none">
  //           <path
  //             d="M15.7727 4.27031C15.025 3.51514 14.1357 2.91486 13.1558 2.50383C12.1758 2.09281 11.1244 1.87912 10.0617 1.875H10C7.84512 1.875 5.77849 2.73102 4.25476 4.25476C2.73102 5.77849 1.875 7.84512 1.875 10V14.375C1.875 14.8723 2.07254 15.3492 2.42417 15.7008C2.77581 16.0525 3.25272 16.25 3.75 16.25H5C5.49728 16.25 5.97419 16.0525 6.32583 15.7008C6.67746 15.3492 6.875 14.8723 6.875 14.375V11.25C6.875 10.7527 6.67746 10.2758 6.32583 9.92417C5.97419 9.57254 5.49728 9.375 5 9.375H3.15313C3.27366 8.07182 3.76315 6.83 4.56424 5.79508C5.36532 4.76016 6.44481 3.97502 7.67617 3.53169C8.90753 3.08836 10.2398 3.0052 11.5167 3.29196C12.7936 3.57872 13.9624 4.22352 14.8859 5.15078C16.0148 6.28539 16.7091 7.78052 16.8477 9.375H15C14.5027 9.375 14.0258 9.57254 13.6742 9.92417C13.3225 10.2758 13.125 10.7527 13.125 11.25V14.375C13.125 14.8723 13.3225 15.3492 13.6742 15.7008C14.0258 16.0525 14.5027 16.25 15 16.25H16.875C16.875 16.7473 16.6775 17.2242 16.3258 17.5758C15.9742 17.9275 15.4973 18.125 15 18.125H10.625C10.4592 18.125 10.3003 18.1908 10.1831 18.3081C10.0658 18.4253 10 18.5842 10 18.75C10 18.9158 10.0658 19.0747 10.1831 19.1919C10.3003 19.3092 10.4592 19.375 10.625 19.375H15C15.8288 19.375 16.6237 19.0458 17.2097 18.4597C17.7958 17.8737 18.125 17.0788 18.125 16.25V10C18.1291 8.93717 17.9234 7.88398 17.5197 6.90077C17.1161 5.91757 16.5224 5.02368 15.7727 4.27031ZM5 10.625C5.16576 10.625 5.32473 10.6908 5.44194 10.8081C5.55915 10.9253 5.625 11.0842 5.625 11.25V14.375C5.625 14.5408 5.55915 14.6997 5.44194 14.8169C5.32473 14.9342 5.16576 15 5 15H3.75C3.58424 15 3.42527 14.9342 3.30806 14.8169C3.19085 14.6997 3.125 14.5408 3.125 14.375V10.625H5ZM15 15C14.8342 15 14.6753 14.9342 14.5581 14.8169C14.4408 14.6997 14.375 14.5408 14.375 14.375V11.25C14.375 11.0842 14.4408 10.9253 14.5581 10.8081C14.6753 10.6908 14.8342 10.625 15 10.625H16.875V15H15Z"
  //             fill="#202020"
  //           />
  //         </svg>
  //       ),
  //       link: 'help'
  //     },
  //     {
  //       name: t('Settings'),
  //       icon: (
  //         <svg
  //           xmlns="http://www.w3.org/2000/svg"
  //           width="20"
  //           height="20"
  //           viewBox="0 0 20 20"
  //           fill="none">
  //           <path
  //             d="M10 6.24966C9.25835 6.24966 8.53332 6.46959 7.91664 6.88165C7.29995 7.2937 6.81931 7.87937 6.53548 8.5646C6.25165 9.24982 6.17739 10.0038 6.32208 10.7312C6.46678 11.4587 6.82393 12.1269 7.34838 12.6513C7.87282 13.1758 8.54101 13.5329 9.26844 13.6776C9.99587 13.8223 10.7499 13.748 11.4351 13.4642C12.1203 13.1804 12.706 12.6997 13.118 12.083C13.5301 11.4664 13.75 10.7413 13.75 9.99966C13.749 9.00542 13.3536 8.05219 12.6505 7.34915C11.9475 6.64611 10.9943 6.25069 10 6.24966ZM10 12.4997C9.50557 12.4997 9.02222 12.353 8.6111 12.0783C8.19998 11.8036 7.87955 11.4132 7.69033 10.9564C7.50111 10.4996 7.4516 9.99689 7.54806 9.51193C7.64453 9.02698 7.88263 8.58152 8.23226 8.23189C8.58189 7.88226 9.02735 7.64416 9.5123 7.5477C9.99725 7.45123 10.4999 7.50074 10.9567 7.68996C11.4135 7.87918 11.804 8.19961 12.0787 8.61073C12.3534 9.02186 12.5 9.50521 12.5 9.99966C12.5 10.6627 12.2366 11.2986 11.7678 11.7674C11.299 12.2363 10.6631 12.4997 10 12.4997ZM18.5891 8.37544C18.5717 8.28743 18.5355 8.2042 18.4831 8.1314C18.4306 8.05861 18.3632 7.99795 18.2852 7.95357L15.9547 6.62544L15.9453 3.99888C15.9451 3.90842 15.9251 3.8191 15.887 3.73709C15.8488 3.65508 15.7933 3.58234 15.7242 3.52388C14.8789 2.8088 13.9054 2.2608 12.8555 1.90903C12.7728 1.88105 12.6852 1.87069 12.5983 1.87863C12.5113 1.88657 12.427 1.91263 12.3508 1.95513L10 3.26919L7.6469 1.95278C7.57061 1.91005 7.48621 1.88379 7.39914 1.87572C7.31207 1.86764 7.22428 1.87793 7.14143 1.90591C6.09233 2.26025 5.11994 2.81033 4.27581 3.527C4.20688 3.58538 4.15142 3.658 4.11325 3.73986C4.07508 3.82173 4.05511 3.9109 4.05471 4.00122L4.043 6.63013L1.71253 7.95825C1.63456 8.00264 1.56706 8.06329 1.51462 8.13609C1.46218 8.20889 1.42603 8.29212 1.40862 8.38013C1.19534 9.45191 1.19534 10.5552 1.40862 11.627C1.42603 11.715 1.46218 11.7982 1.51462 11.871C1.56706 11.9438 1.63456 12.0045 1.71253 12.0489L4.043 13.377L4.05237 16.0036C4.05265 16.094 4.07257 16.1833 4.11074 16.2654C4.14891 16.3474 4.20443 16.4201 4.27346 16.4786C5.11883 17.1936 6.09234 17.7416 7.14221 18.0934C7.22489 18.1214 7.31252 18.1318 7.39944 18.1238C7.48636 18.1159 7.57066 18.0898 7.6469 18.0473L10 16.7301L12.3532 18.0465C12.4463 18.0984 12.5512 18.1253 12.6578 18.1247C12.7261 18.1246 12.7939 18.1136 12.8586 18.0918C13.9076 17.738 14.88 17.1884 15.7242 16.4723C15.7932 16.4139 15.8486 16.3413 15.8868 16.2595C15.925 16.1776 15.9449 16.0884 15.9453 15.9981L15.9571 13.3692L18.2875 12.0411C18.3655 11.9967 18.433 11.936 18.4854 11.8632C18.5379 11.7904 18.574 11.7072 18.5914 11.6192C18.8035 10.5483 18.8027 9.44606 18.5891 8.37544ZM17.4172 11.1028L15.1852 12.3723C15.0874 12.4279 15.0064 12.5089 14.9508 12.6067C14.9055 12.6848 14.8578 12.7676 14.8094 12.8458C14.7474 12.9443 14.7144 13.0583 14.7141 13.1747L14.7024 15.6942C14.1024 16.1653 13.434 16.5421 12.7203 16.8114L10.4688 15.5567C10.3753 15.505 10.2701 15.4781 10.1633 15.4786H10.1485C10.0539 15.4786 9.95862 15.4786 9.86409 15.4786C9.75228 15.4758 9.64174 15.5027 9.54378 15.5567L7.29065 16.8145C6.57543 16.5473 5.9052 16.1724 5.30315 15.7028L5.29456 13.1872C5.29417 13.0705 5.26116 12.9563 5.19925 12.8575C5.15081 12.7793 5.10315 12.7012 5.05862 12.6184C5.0034 12.5191 4.92244 12.4365 4.82425 12.3793L2.58987 11.1067C2.47424 10.3753 2.47424 9.63028 2.58987 8.89888L4.818 7.627C4.91579 7.5714 4.99676 7.49042 5.05237 7.39263C5.09768 7.3145 5.14534 7.23169 5.19378 7.15357C5.25578 7.05505 5.28881 6.94107 5.28909 6.82466L5.30081 4.30513C5.90075 3.83399 6.56914 3.45725 7.28284 3.18794L9.53128 4.44263C9.62913 4.49687 9.73975 4.52385 9.85159 4.52075C9.94612 4.52075 10.0414 4.52075 10.136 4.52075C10.2478 4.52355 10.3583 4.49659 10.4563 4.44263L12.7094 3.18482C13.4246 3.45206 14.0949 3.82696 14.6969 4.29653L14.7055 6.81216C14.7059 6.9288 14.7389 7.043 14.8008 7.14185C14.8492 7.21997 14.8969 7.2981 14.9414 7.38091C14.9966 7.48022 15.0776 7.5628 15.1758 7.61997L17.4102 8.89263C17.5274 9.62459 17.5287 10.3704 17.4141 11.1028H17.4172Z"
  //             fill="#202020"
  //           />
  //         </svg>
  //       ),
  //       link: 'settings'
  //     }
  //   ]
  // };

  const renderNavGroup = (group, isActive) => (
    <React.Fragment key={group.title}>
      {group.items.map((item) => {
        const isItemActive = isActive(item);
        return (
          <NavItem key={item.link} onClick={() => handleClick(item.link)} active={isItemActive}>
            {React.cloneElement(item.icon, {
              color: isItemActive ? 'primary' : 'action'
            })}
            <NavItemText variant="body2" active={isItemActive}>
              {item.name}
            </NavItemText>
          </NavItem>
        );
      })}
    </React.Fragment>
  );

  const renderContent = () => {
    switch (searchTerm) {
      case 'dashboard':
        return (
          <>
            <Helmet>
              <title>{t('Dashboard')}</title>
            </Helmet>
            <Dashboard />
          </>
        );
      case 'listing':
        return (
          <>
            <Helmet>
              <title>{t('Listing Management')}</title>
            </Helmet>
            <ListingManagement />
          </>
        );
      case 'analytics':
        return (
          <>
            <Helmet>
              <title>{t('Analytics and Insights')}</title>
            </Helmet>
            <MarketAnalytics />
          </>
        );
      case 'communication':
        return (
          <>
            <Helmet>
              <title>{t('Communication Hub')}</title>
            </Helmet>
            <MyContact />
          </>
        );
      case 'dms':
        return (
          <>
            <Helmet>
              <title>{t('DMS')}</title>
            </Helmet>
            <Document />
          </>
        );
      case 'onboarding':
        return (
          <>
            <Helmet>
              <title>{t('Onboarding for Landlords')}</title>
            </Helmet>
            <Onboarding />
          </>
        );
      // case 'help':
      //   return <Typography variant="h4">{t('Help')}</Typography>;
      // case 'settings':
      //   return <Typography variant="h4">{t('Settings')}</Typography>;
      default:
        return <Typography variant="h4">{t('Page not found')}</Typography>;
    }
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <Sidebar>
        <MainNavigation>
          {mainNavGroups.map((group, index) => (
            <React.Fragment key={group.title || index}>
              {index > 0 && <GroupDivider />}
              {renderNavGroup(group, (item) => searchTerm === item.link)}
            </React.Fragment>
          ))}
        </MainNavigation>
        {/* <BottomNavigation>
          <GroupDivider />
          {renderNavGroup(accountNavGroup, (item) => searchTerm === item.link)}
        </BottomNavigation> */}
      </Sidebar>
      {/* Content area */}
      <Box
        sx={{
          flexGrow: 1,
          px: !['communication', 'dms', 'onboarding'].includes(searchTerm) && 4,
          py: !['communication', 'dms', 'onboarding'].includes(searchTerm) && 3,
          background: '#FCFCFD'
        }}
      >
        {renderContent()}
      </Box>
    </Box>
  );
};

export default OwnerAccount;
